import { SiteRoute } from '@journeyapps/package-types';
import styled from '@emotion/styled';
import * as React from 'react';
import { Link } from 'react-router';
import { MainMenuItems } from '../../MainMenuItems';
import { IPAD_MEDIA, PHONE_MEDIA } from '../../media';
import { MdxLink } from '../../pages/shared/MdxLink';

const logo = require('../../../media/journeyapps-platform-logo.svg');

const exploreLinks = [...MainMenuItems.filter((item) => !item.items?.length && item.to !== '#')];
const navigationGroups = [
  { label: 'Explore', items: exploreLinks },
  ...MainMenuItems.flatMap((item) =>
    item.items?.length ? [{ label: item.label, items: item.items.filter((child) => child.to !== '#') }] : []
  )
];
const companyItems = MainMenuItems.find((item) => item.label === 'Company')?.items ?? [];
const socialLinks = companyItems.find((item) => item.label === 'Socials')?.items ?? [];
const legalLinks = [
  ...(companyItems.find((item) => item.label === 'Legal')?.items ?? []),
  { label: 'AI manifesto', to: SiteRoute.AI_MANIFESTO }
];

export interface FooterWidgetProps {
  className?: string;
}

export const FooterWidget: React.FC<FooterWidgetProps> = ({ className }) => (
  <S.Footer className={className}>
    <S.Inner>
      <S.Brand to={SiteRoute.HOMEPAGE}>
        <S.Logo src={logo} alt="JourneyApps Platform" />
      </S.Brand>

      <S.Navigation aria-label="Footer navigation">
        {navigationGroups.map((group) => (
          <S.Group key={group.label}>
            <S.GroupHeading>{group.label}</S.GroupHeading>
            <S.GroupLinks>
              {group.items.map((item) => (
                <S.Item key={item.label} href={item.to}>
                  {item.label}
                </S.Item>
              ))}
            </S.GroupLinks>
          </S.Group>
        ))}
      </S.Navigation>

      <S.Bottom>
        <S.Copyright>© {new Date().getFullYear()} Journey Mobile, Inc. All rights reserved.</S.Copyright>
        <S.BottomLinks aria-label="Social links">
          {socialLinks.map((item) => (
            <S.SmallLink key={item.label} href={item.to}>
              {item.label}
            </S.SmallLink>
          ))}
        </S.BottomLinks>
        <S.BottomLinks aria-label="Legal links">
          {legalLinks.map((item) => (
            <S.SmallLink key={item.label} href={item.to}>
              {item.label}
            </S.SmallLink>
          ))}
        </S.BottomLinks>
      </S.Bottom>
    </S.Inner>
  </S.Footer>
);

namespace S {
  export const Footer = styled.footer`
    width: 100%;
    flex: 0 0 auto;
    box-sizing: border-box;
    background: ${(p) => p.theme.footer.background};
    border-top: 1px solid ${(p) => p.theme.surface.border};
  `;

  export const Inner = styled.div`
    max-width: 1400px;
    margin: 0 auto;
    padding: 44px 24px 28px;
    box-sizing: border-box;
  `;

  export const Brand = styled(Link)`
    display: inline-flex;
    align-items: center;
  `;

  export const Logo = styled.img`
    display: block;
    height: 32px;
    max-width: 100%;
    filter: ${(p) => (p.theme.light ? 'invert()' : 'none')};
  `;

  export const Navigation = styled.nav`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: 32px;
    padding: 40px 0 48px;
    ${IPAD_MEDIA} {
      grid-template-columns: repeat(3, minmax(0, 1fr));
    }
    ${PHONE_MEDIA} {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  `;

  export const Group = styled.div`
    min-width: 0;
  `;

  export const GroupHeading = styled.h2`
    color: ${(p) => p.theme.text.heading};
    font-size: 14px;
    font-weight: 650;
    margin: 0 0 18px;
  `;

  export const GroupLinks = styled.div`
    display: grid;
    gap: 13px;
  `;

  export const Item = styled(MdxLink)`
    width: fit-content;
    color: ${(p) => p.theme.text.paragraph};
    font-size: 14px;
    line-height: 1.4;
    text-decoration: none;
    &:hover {
      color: ${(p) => p.theme.text.heading};
    }
  `;

  export const Bottom = styled.div`
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 16px 28px;
    padding-top: 24px;
    border-top: 1px solid ${(p) => p.theme.surface.border};
  `;

  export const Copyright = styled.span`
    color: ${(p) => p.theme.text.paragraph};
    font-size: 12px;
    margin-right: auto;
  `;

  export const BottomLinks = styled.nav`
    display: flex;
    flex-wrap: wrap;
    gap: 10px 16px;
  `;

  export const SmallLink = styled(MdxLink)`
    color: ${(p) => p.theme.text.paragraph};
    font-size: 12px;
    text-decoration: none;
    &:hover {
      color: ${(p) => p.theme.text.heading};
    }
  `;
}
